import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { message, DatePicker, Spin } from "antd";
import Select from "react-select";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { RotateCcw } from "feather-icons-react";
import Table from "../../../core/pagination/datatable.jsx";
import ImageWithBasePath from "../../../core/img/imagewithbasebath";
import CommonFooter from "../../../core/common/footer/commonFooter.jsx";
import "jspdf-autotable";
import dayjs from "dayjs";
import axios from "axios";
import { ROLL_CALL_API_BASE_URL } from "../RollCall/RollCallConfig/RollCallConfig.js";

const { RangePicker } = DatePicker;

const KpiMetricsReports = () => {
  const [tokenData, setTokenData] = useState(null);
  const [superIdData, setSuperIdData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState(null);
  const [data, setData] = useState([]);
  const navigate = useNavigate();

  const columns = [
    {
      title: "Branch ",
      dataIndex: "BranchName",
      key: "BranchName",
      render: (value) => value || "-",
    },
    {
      title: "KPI",
      dataIndex: "RuleName",
      key: "RuleName",
      render: (value) => value || "-",
    },

    {
      title: "Date",
      dataIndex: "DateofTransaction",
      key: "DateofTransaction",
      render: (value) => (value ? dayjs(value).format("DD-MM-YYYY") : "-"),
    },
    {
      title: "Achieved",
      dataIndex: "TotalValue",
      key: "TotalValue",
      render: (value) => value || 0,
    },
    {
      title: "Points",
      dataIndex: "Points",
      key: "Points",
      render: (value) => (value !== null && value !== undefined ? value : 0),
    },
  ];

  const rangePresets = [
    {
      label: "Today",
      value: [dayjs().startOf("day"), dayjs().endOf("day")],
    },
    {
      label: "This Week",
      value: [dayjs().startOf("week"), dayjs().endOf("week")],
    },
    {
      label: "This Month",
      value: [dayjs().startOf("month"), dayjs().endOf("month")],
    },
    {
      label: "This Year",
      value: [dayjs().startOf("year"), dayjs().endOf("year")],
    },
  ];

  const handleRefresh = () => {
    // Reset filters
    setSelectedBranch(0);
    setDateRange(null);

    // Fetch fresh data
    fetchKpiMetricsReport({
      branchId: 0,
      fromDate: undefined,
      toDate: undefined,
    });
  };

  const onRangeChange = (dates) => {
    setDateRange(dates);

    if (dates) {
      fetchKpiMetricsReport({
        branchId: selectedBranch || 0,
        fromDate: dates[0].format("YYYY-MM-DD"),
        toDate: dates[1].format("YYYY-MM-DD"),
      });
    } else {
      fetchKpiMetricsReport({
        branchId: selectedBranch || 0,
        fromDate: undefined,
        toDate: undefined,
      });
    }
  };

  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState("0");
  const [loadingBranches, setLoadingBranches] = useState(false);

  useEffect(() => {
    const token = sessionStorage.getItem("authToken");
    const superId = sessionStorage.getItem("superId");
    if (token && superId) {
      setTokenData(token);
      setSuperIdData(superId);
    } else {
      console.warn("User data not found in sessionStorage");
      navigate("/signin");
    }
  }, [navigate]);

  const fetchKpiMetricsReport = async (filters = {}) => {
    if (!superIdData || !tokenData) return;

    setLoading(true);

    try {
      const { data } = await axios.get(
        `${ROLL_CALL_API_BASE_URL}portal/kpi/getkpiResultsReport`,
        {
          params: {
            SuperId: superIdData,
            BranchId: filters?.branchId || 0,
            FromDate: filters?.fromDate,
            Todate: filters?.toDate,
          },
          headers: {
            Authorization: `Bearer ${tokenData}`,
          },
        },
      );

      if (data?.status) {
        setData(data.ResultData || []);
      } else {
        setData([]);
      }
    } catch (err) {
      console.error(err);
      message.error("Failed to load KPI report");
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!superIdData || !tokenData) return;

    fetchKpiMetricsReport({
      branchId: selectedBranch,
      fromDate: dateRange?.[0]?.format("YYYY-MM-DD"),
      toDate: dateRange?.[1]?.format("YYYY-MM-DD"),
    });
  }, [superIdData, tokenData, selectedBranch, dateRange]);

  const fetchBranches = async () => {
    if (!tokenData || !superIdData) return;

    setLoadingBranches(true);
    try {
      const response = await fetch(
        `${ROLL_CALL_API_BASE_URL}portal/branch/getbranch?superid=${superIdData}`, // Use dynamic superIdData
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${tokenData}`,
            "Content-Type": "application/json",
          },
        },
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          `HTTP Error! Status: ${response.status} - ${errorText.substring(
            0,
            100,
          )}`,
        );
      }

      const data = await response.json();

      if (data?.status && Array.isArray(data.ResultData)) {
        const formattedBranches = data.ResultData.map((branch) => ({
          value: branch.branchid?.toString() || "",
          label:
            `${branch.Name || ""} ${
              branch.code ? `(${branch.code})` : ""
            }`.trim() || "Unnamed Branch",
        }));

        const allOption = { value: "0", label: "All Branches" };
        setBranches([allOption, ...formattedBranches]);
      } else {
        setBranches([{ value: "0", label: "All Branches" }]);
      }
    } catch (error) {
      console.error("Error fetching branches:", error);
      setBranches([{ value: "all", label: "All Branches" }]); // Fallback
    } finally {
      setLoadingBranches(false);
    }
  };
  useEffect(() => {
    fetchBranches();
  }, [tokenData]);

  const handleBranchChange = (option) => {
    setSelectedBranch(option?.value);
  };

  const exportPDF = () => {};
  const exportExcel = () => {};

  return (
    <>
      <style>
        {`
   

    .ctc-table1 .ant-table-thead > tr > th {
      background-color: #d2e6f5ff !important; 
    }

    .ctc-table1 table {
      table-layout: auto;
    }

    .ctc-info-2col {
      display: grid;
      column-gap: 10px;
      row-gap: 8px;
    }

    .ctc-pair {
      display: flex;
      align-items: flex-start;
    }

    .ctc-label {
      font-weight: 600;
    }

    .ctc-value {
      font-weight: 500;
    }
  `}
      </style>
      <div className="page-wrapper">
        <div className="content">
          <div className="page-header">
            <div className="add-item d-flex">
              <div className="page-title">
                <nav aria-label="breadcrumb">
                  <ol className="breadcrumb">
                    <li className="breadcrumb-item">
                      <Link to="/rollcall/dashboard">
                        <i className="fas fa-home fa-lg" />
                      </Link>
                    </li>
                    <h4 className="breadcrumb-item">Kpi Metrics Report</h4>
                  </ol>
                </nav>
                <h6>Manage your Kpi Metrics Report</h6>
              </div>
            </div>

            <ul className="table-top-head d-none">
              <li>
                <OverlayTrigger
                  placement="top"
                  overlay={<Tooltip>PDF</Tooltip>}
                >
                  <Link onClick={exportPDF}>
                    <ImageWithBasePath
                      src="assets/img/icons/pdf.svg"
                      alt="pdf"
                    />
                  </Link>
                </OverlayTrigger>
              </li>
              <li>
                <OverlayTrigger
                  placement="top"
                  overlay={<Tooltip>Excel</Tooltip>}
                >
                  <Link onClick={exportExcel}>
                    <ImageWithBasePath
                      src="assets/img/icons/excel.svg"
                      alt="excel"
                    />
                  </Link>
                </OverlayTrigger>
              </li>
              <li>
                <OverlayTrigger
                  placement="top"
                  overlay={<Tooltip>Refresh</Tooltip>}
                >
                  <Link onClick={handleRefresh}>
                    <RotateCcw />
                  </Link>
                </OverlayTrigger>
              </li>
            </ul>
          </div>

          <div className="card table-list-card">
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
              <div className="search-set col-lg-auto col-md-12 mb-md-1 mb-sm-1 mb-lg-0"></div>
              <div className="col-lg-auto col-md-12 mt-md-4 mt-sm-4 mt-lg-0">
                <div className="d-flex table-dropdown my-xl-auto right-content align-items-center flex-wrap row-gap-3">
                  {/* Year */}
                  <div className="me-2">
                    <RangePicker
                      format="DD-MM-YYYY"
                      presets={rangePresets}
                      style={{ height: 38 }}
                      onChange={onRangeChange}
                    />
                  </div>

                  <div className="me-2">
                    <Select
                      placeholder="Select Branch"
                      style={{ width: 222, height: 38 }}
                      defaultValue="All Branches"
                      loading={loadingBranches}
                      onChange={handleBranchChange}
                      options={branches}
                      disabled={loadingBranches}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="card-body pb-0">
              <div className="table-responsive">
                {loading ? (
                  <div className="text-center my-4">
                    <Spin size="large" />
                    <p>Loading data...</p>
                  </div>
                ) : data.length > 0 ? (
                  <Table
                    columns={columns}
                    dataSource={data}
                    rowKey="id"
                    pgno={true}
                  />
                ) : (
                  <p className="text-center my-4">No data available</p>
                )}
              </div>
            </div>
          </div>
        </div>

        <CommonFooter />
      </div>
    </>
  );
};

export default KpiMetricsReports;
